// A banki kontír-szerkesztő ("Adott bankkivonat kontírozási tételei",
// K2D w_popup_bank_accitem_os) modelljei. A tétel az imaccbankaccitem sor képe,
// a bontás az imaccbankaccit_i_d soré — a dimCode SZÖVEGES kód (nvarchar(10)).
// A számla-oldali bontással szemben itt nincs parts_id.

export interface BankPreAccEditDimension {
  id: number;            // szam; <= 0 = új (negatív temp id)
  head: number;          // kontírtétel szam (banknál mindig létező, pozitív id)
  dimCodeType: number;   // csaccdimcodetype: 5 = IV. ... 13 = XII.
  dimCode: string;
  total: number;

  extProdParts: number;
  extProdPartsCode: string;
  extProdPartsName: string;
  productParts: number;
  productPartsCode: string;
  productPartsName: string;
  production: number;
  stock: number;
  carId: number;
  regNum: string;
  userId: number;
  userName: string;
  stationId: number;
  stationName: string;
  toolId: number;
  toolName: string;
  project: number;
  projectName: string;
  projectMainCode: string;
  projectCode: string;
  poId: number;
  poNumber: string;
  workNumber: string;
  extProdPartner: number;
  extProdPartnerName: string;

  inEdit: boolean;
  selected: boolean;
  deleting: boolean;
}

export const BankPreAccEditDimensionDefaultProps: BankPreAccEditDimension = {
  id: 0,
  head: 0,
  dimCodeType: 5,
  dimCode: "",
  total: 0,

  extProdParts: 1,
  extProdPartsCode: "",
  extProdPartsName: "",
  productParts: 1,
  productPartsCode: "",
  productPartsName: "",
  production: 1,
  stock: 1,
  carId: 1,
  regNum: "",
  userId: 1,
  userName: "",
  stationId: 1,
  stationName: "",
  toolId: 1,
  toolName: "",
  project: 1,
  projectName: "",
  projectMainCode: "",
  projectCode: "",
  poId: 1,
  poNumber: "",
  workNumber: "",
  extProdPartner: 1,
  extProdPartnerName: "",

  inEdit: false,
  selected: false,
  deleting: false,
};

// Egy kontírtétel (K2D d_g_i_bankaccitem_os sora). K2D-hűen csak az
// accCode / detailedType / dimCode1-3 / note szerkeszthető; tétel felvétele
// és törlése NINCS — sort csak az spm_bank_cont generál.
export interface BankPreAccEditItem {
  id: number;
  accountItems: number;
  accCode: string;
  accCodeName: string;
  accSign: string;       // '+' = Tartozik, '-' = Követel
  detailedType: number;
  total: number;
  currency: string;
  accDate: string;
  note: string;
  dimCode1: string;
  dimCode2: string;
  dimCode3: string;
  invCode: string;
  prefix: string;        // a hivatkozott számla magyar prefixe
  invNo: number;
  outerInvNo: string;
  payment: number;
  period: number;
  accType: number;
  accTypeName: string;
  partnerName: string;
  crediting: boolean;    // a kivonat-tétel jóváírás-e (K2D total-háttérszín)

  inEdit: boolean;
  selected: boolean;
}

export const BankPreAccEditItemDefaultProps: BankPreAccEditItem = {
  id: 0,
  accountItems: 0,
  accCode: "",
  accCodeName: "",
  accSign: "+",
  detailedType: 0,
  total: 0,
  currency: "HUF",
  accDate: "",
  note: "",
  dimCode1: "",
  dimCode2: "",
  dimCode3: "",
  invCode: "",
  prefix: "",
  invNo: 0,
  outerInvNo: "",
  payment: 0,
  period: 1,
  accType: 0,
  accTypeName: "",
  partnerName: "",
  crediting: false,

  inEdit: false,
  selected: false,
};

// A szerkesztő read-only fej-panelje (K2D d_f_bank_os)
export interface BankPreAccEditorHead {
  id: number;
  status: number;
  statusName: string;
  accStatus: number;
  accStatusName: string;
  bankName: string;
  accountNo: string;
  extract: string;
  edate: string;
  openBalance: number;
  closeBalance: number;
  currency: string;
  income: number;   // K2D BEVETEL
  expense: number;  // K2D KIADAS
}

export const BankPreAccEditorHeadDefaultProps: BankPreAccEditorHead = {
  id: 0,
  status: 1,
  statusName: "",
  accStatus: 1,
  accStatusName: "",
  bankName: "",
  accountNo: "",
  extract: "",
  edate: "",
  openBalance: 0,
  closeBalance: 0,
  currency: "HUF",
  income: 0,
  expense: 0,
};

// A kivonat tételei (imaccaccountitems, status=3) az egyenleg-panelhez
export interface BankPreAccStatementItem {
  id: number;
  partnerName: string;
  amount: number;
  crediting: boolean;
  accTypeName: string;
}

export interface BankPreAccAccNo {
  id: number;
  accNo: string;
  name: string;
  mainTypeName: string;
  detailedType: number;
}

export interface BankPreAccDimCode {
  id: number;
  dimcode: string;
  national: string;
  type: number;
}

export interface BankPreAccEditorFlags {
  extBook: boolean;
  dim49: boolean;
  checkDetType: boolean;
  // K2D pssyssysfile1.is_up_changeable — a "Feladás tételei" gomb láthatósága
  upChangeable: boolean;
  dimTypeLive: Record<number, boolean>;
  // csaccdimcodetype.required — a K2D f_dimcode_check_os csak ezeken kényszeríti ki a dimkódot
  dimTypeRequired: Record<number, boolean>;
}

export interface BankPreAccEditorData {
  head: BankPreAccEditorHead;
  items: BankPreAccEditItem[];
  dimensions: BankPreAccEditDimension[];
  statementItems: BankPreAccStatementItem[];
  originalAccStatus: number;
  editable: boolean;       // status=3 és acc_status ∈ {2,5,7,8,9}
  reAccountable: boolean;  // acc_status ∈ {2,3,5,7,8,9}
  uploadable: boolean;     // K2D pb_felad kapu: acc_status ∉ {4,6}
  accNos: BankPreAccAccNo[];
  dimCodes: BankPreAccDimCode[];
  detailedTypes: { id: number; name: string }[];
  flags: BankPreAccEditorFlags;
}

//----------- Mentés (K2D pb_ok) -----------//

export interface BankPreAccSaveRequest {
  originalAccStatus: number;
  items: {
    id: number;
    accCode: string;
    detailedType: number;
    dimCode1: string;
    dimCode2: string;
    dimCode3: string;
    note: string;
  }[];
  dimensions: {
    id: number;
    head: number;
    dimCodeType: number;
    dimCode: string;
    total: number;
    project: number;
    poId: number;
    userId: number;
    stationId: number;
    toolId: number;
    carId: number;
    extProdParts: number;
    extProdPartner: number;
    productParts: number;
    production: number;
    stock: number;
  }[];
  deletedDimensionIds: number[];
}

export interface BankPreAccSaveResult {
  accStatus: number;
  accStatusName: string;
  statusChanged: boolean;
  errors: { id: number; errType: number; errTypeName: string; note: string }[];
}

// A dim-fülek kísérő-lookupjai
export interface BankPreAccCar {
  id: number;
  reqNum: string;
  bodyNo: string;
  owner: string;
  modell: string;
}

export interface BankPreAccProject {
  id: number;
  mainCode: string;
  code: string;
  name: string;
}

export interface BankPreAccPo {
  id: number;
  workNumber: string;
  poNumber: string;
  project: number;
  extCode: string;
}

export interface BankPreAccTool {
  id: number;
  code: string;
  name: string;
  groupCode: string;
  groupName: string;
}

export interface BankPreAccValidate {
  id: number;
  name: string;
}

//----------- Feladás-tételek (K2D pb_felad → w_popup_item_up_os) -----------//

// A kivonat egy banki tétele (imaccaccountitems, status=3) a feladás-pipával;
// az isUp a kontírsorok is_up flagjének K2D-hű aggregátuma
export interface BankPreAccUploadItem {
  id: number;
  partnerName: string;
  amount: number;
  currency: string;
  crediting: boolean;
  accTypeName: string;
  comment: string;
  isUp: boolean;
}

export interface BankPreAccUploadFlagsResult {
  updatedItems: number;
}
